import React, { useState, useEffect } from 'react'
import { ProcedureTypeService } from '../../services/procedureTypeService'
import type { ProcedureType } from '../../services/procedureTypeService'

interface ProcedureTypesPageProps {
  onNavigate: (page: string, data?: any) => void
}

export function ProcedureTypesPage({ onNavigate }: ProcedureTypesPageProps) {
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter states
  const [keyword, setKeyword] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined)
  const [sortBy, setSortBy] = useState('procedureTypeCode_asc')

  useEffect(() => {
    loadProcedureTypes()
  }, [keyword, categoryFilter, activeFilter, sortBy])

  const loadProcedureTypes = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await ProcedureTypeService.getAll({
        keyword: keyword || undefined,
        categoryCode: categoryFilter || undefined,
        active: activeFilter
      })

      // フロントエンドでソート
      const sorted = [...data.items].sort((a, b) => {
        const [field, direction] = sortBy.split('_')
        let aValue: any = a[field as keyof ProcedureType]
        let bValue: any = b[field as keyof ProcedureType]

        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })

      setProcedureTypes(sorted)
    } catch (err) {
      console.error('Failed to load procedure types:', err)
      setError('申請種別の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    onNavigate('procedure-type-detail')
  }

  const handleEdit = (procedureTypeCode: string) => {
    onNavigate('procedure-type-detail', { procedureTypeCode })
  }

  const SORT_OPTIONS = [
    { value: 'procedureTypeCode_asc', label: '申請種別コード（A-Z）' },
    { value: 'procedureTypeCode_desc', label: '申請種別コード（Z-A）' },
    { value: 'procedureName_asc', label: '申請種別名（A-Z）' },
    { value: 'procedureName_desc', label: '申請種別名（Z-A）' }
  ]

  return (
    <div className="px-8 py-6">
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">申請種別管理</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* キーワード検索 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>キーワード検索</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="コードまたは名称で検索"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {/* カテゴリ */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>カテゴリ</label>
            <input
              type="text"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              placeholder="カテゴリで絞り込み"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {/* ステータス */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ステータス</label>
            <select
              value={activeFilter === undefined ? '' : activeFilter ? 'true' : 'false'}
              onChange={(e) => setActiveFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <option value="">すべて</option>
              <option value="true">有効のみ</option>
              <option value="false">無効のみ</option>
            </select>
          </div>
        </div>

        {/* ソート選択と件数表示 */}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 7L12 3L16 7M8 17L12 21L16 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '2rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6'
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {procedureTypes.length}件の申請種別
          </div>
        </div>
      </div>

      {/* 作成ボタン */}
      <div className="mb-4">
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          + 新規作成
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : procedureTypes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">申請種別がありません</div>
        ) : (
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>申請種別コード</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>申請種別名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>カテゴリ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>表示順</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {procedureTypes.map((type) => (
                <tr key={type.procedureTypeCode} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {type.procedureTypeCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      type.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {type.active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {type.procedureName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {type.category || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {type.displayOrder}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => handleEdit(type.procedureTypeCode)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      開く
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
